import { ResponseModelInterfaceStatic } from "@/service/models/response-model-interface";
import { LastingDosageVM } from "@/service/view-models/dosage-choices/lasting-dosage-vm";
import { staticImplements } from "@/utilities/static-implements";
import { Duration } from "@/utilities/types/duration";
import { XmlDocumentWrapper } from "@/utilities/xml-helpers/xml-document-wrapper";
import { XmlElementWrapper } from "@/utilities/xml-helpers/xml-element-wrapper";
import { BaseDosageChoice } from ".";
import { FormulationAndRoute } from "../parts/formulation-and-route";

@staticImplements<ResponseModelInterfaceStatic>()
export class LastingDosage extends BaseDosageChoice
{
    public constructor(
        public interval: Duration,
        public doseValue: number,
        public doseUnit: string,
        public doseInfusionTime: Duration|null,
        public formulationAndRoute: FormulationAndRoute,
    )
    {
        super();
    }

    public get intervalMinutes(): number
    {
        return this.interval.minutes;
    }

    public set intervalMinutes(value: number)
    {
        this.interval = Duration.fromMinutes(value);
    }

    /**
     * @inheritdoc
     */
    public makeViewModel(): LastingDosageVM
    {
        return new LastingDosageVM().fillFromModel(this);
    }

    /**
     * @inheritdoc
     */
    public getClosestNextDoseDate(knownDose: Date, current: Date): Date
    {
        return this.interval.alignedDateAfter(current, knownDose);
    }

    /**
     * @inheritdoc
     */
    public getClosestPreviousDoseDate(knownDose: Date, current: Date): Date
    {
        return this.interval.alignedDateBefore(current, knownDose);
    }

    /**
     * @inheritdoc
     */
    public getDoseValueForDisplay(): number
    {
        return this.doseValue;
    }

    /**
     * @inheritdoc
     */
    public getDoseUnitForDisplay(): string
    {
        return this.doseUnit;
    }

    /**
     * @inheritdoc
     */
    public getInfusionTimeForDisplay(): Duration|null
    {
        return this.doseInfusionTime;
    }

    /**
     * @inheritdoc
     */
    public getIntervalForDisplay(): Duration
    {
        return this.interval;
    }

    /**
     * @inheritdoc
     */
    public getRouteForDisplay(): FormulationAndRoute
    {
        return this.formulationAndRoute;
    }

    /**
     * @inheritdoc
     */
    public static makeFromXml(elem: XmlElementWrapper): LastingDosage
    {
        const doseElem = elem.firstChildByTag("dose");
        const infusionTimeElem = doseElem.firstChildByTagOrNull("infusionTimeInMinutes");

        const infusionTime = (infusionTimeElem === null || infusionTimeElem.valueAsNumber() === 0)
            ? null
            : Duration.fromMinutes(infusionTimeElem.valueAsNumber());

        return new this(
            Duration.fromString(elem.firstChildByTag("interval").value()),
            doseElem.firstChildByTag("value").valueAsNumber(),
            doseElem.firstChildByTag("unit").value(),
            infusionTime,
            FormulationAndRoute.makeFromXml(elem.firstChildByTag("formulationAndRoute")),
        );
    }

    /**
     * @inheritdoc
     */
    public getXml(doc: XmlDocumentWrapper): XmlElementWrapper
    {
        const element = doc.createElement("lastingDosage");

        element.appendNewElement("interval", this.interval.toString());
        
        const doseElement = element.appendNewElement("dose");
        doseElement.appendNewElement("value", this.doseValue.toString());
        doseElement.appendNewElement("unit", this.doseUnit);

        const infusionTimeInMinutes = (this.doseInfusionTime !== null) ? this.doseInfusionTime.minutes.toFixed(0) : '0';
        doseElement.appendNewElement("infusionTimeInMinutes", infusionTimeInMinutes);

        element.append(this.formulationAndRoute.getXml(doc));

        return element;
    }
}
